import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { Rnd } from 'react-rnd';
import { FONT_OPTIONS } from './tvTemplates';
import FontSelect from './FontSelect';
import './tvPreview.css';

const DISPLAY_MAX_HEIGHT = 900;

const bindingToText = (binding, tvContent, element) => {
  switch (binding) {
    case 'title':
      return tvContent.title || 'Add a title';
    case 'subtitle':
      return tvContent.subtitle || '';
    case 'message':
      return tvContent.message || 'Message copy goes here\nUse line breaks for clarity.';
    case 'footer':
      return tvContent.footer || 'Powered by Suitestream.tv';
    case 'qrLabel':
      return tvContent.qrLabel || '';
    default:
      // Custom / static bindings: prefer staticText on the element, then tvContent lookup
      return element?.style?.staticText || (tvContent?.[binding]) || '';
  }
};

const canvasDimensions = (canvas = {}) => {
  const width = canvas.width || (canvas.orientation === 'portrait' ? 1080 : 1920);
  const height = canvas.height || (canvas.orientation === 'portrait' ? 1920 : 1080);
  return { width, height };
};

const getFontOptions = (selected) => {
  if (!selected || FONT_OPTIONS.some((option) => option.value === selected)) return FONT_OPTIONS;
  return [...FONT_OPTIONS, { value: selected, label: selected }];
};

export default function TVLayoutEditor({ layout, tvContent, imageUrl, landingUrl, onChange, onContentChange }) {
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const textRefs = useRef(new Map());
  const editingInitialValue = useRef(new Map());
  const [qrDataUrl, setQrDataUrl] = useState(null);

  const canvas = useMemo(() => canvasDimensions(layout.canvas), [layout.canvas]);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(() => canvas.width);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = node.clientWidth || canvas.width;
      setContainerWidth(nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width || canvas.width);
        }
      });
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [canvas.width]);

  useEffect(() => {
    let cancelled = false;
    if (!landingUrl) {
      setQrDataUrl(null);
      return () => {
        cancelled = true;
      };
    }

    QRCode.toDataURL(landingUrl, { errorCorrectionLevel: 'Q' })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [landingUrl]);

  const scale = useMemo(() => {
    if (!canvas.width || !canvas.height) return 1;
    const widthRatio = containerWidth ? containerWidth / canvas.width : 1;
    const heightRatio = DISPLAY_MAX_HEIGHT / canvas.height;
    const nextScale = Math.min(1, widthRatio, heightRatio);
    return Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1;
  }, [canvas.width, canvas.height, containerWidth]);

  const stageStyle = useMemo(
    () => ({
      width: canvas.width * scale,
      height: canvas.height * scale,
      position: 'relative',
      background: layout.canvas?.background || '#0f172a',
      backgroundImage: layout.canvas?.backgroundImage || 'none',
      borderRadius: Math.max(10, 24 * scale),
      boxShadow: '0 32px 80px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.06)',
      overflow: 'hidden',
    }),
    [canvas.width, canvas.height, scale, layout.canvas]
  );

  const elements = useMemo(() => (Array.isArray(layout.elements) ? layout.elements : []), [layout.elements]);

  useEffect(() => {
    if (selectedId && !elements.find((element) => element.id === selectedId)) {
      setSelectedId(null);
    }
  }, [elements, selectedId]);

  useEffect(() => {
    elements.forEach((element) => {
      if (element.type !== 'text') return;
      const node = textRefs.current.get(element.id);
      if (!node || editingId === element.id) return;
      const nextValue = bindingToText(element.binding, tvContent, element) || '';
      if (node.textContent !== nextValue) {
        node.textContent = nextValue;
      }
    });
  }, [elements, tvContent, editingId]);

  const updateLayout = useCallback(
    (updater) => {
      if (!onChange) return;
      const nextLayout = updater(layout);
      onChange(nextLayout);
    },
    [layout, onChange]
  );

  const updateElement = useCallback(
    (id, mutator) => {
      updateLayout((current) => {
        const updatedElements = current.elements.map((element) =>
          element.id === id ? mutator(element) : element
        );
        return { ...current, elements: updatedElements };
      });
    },
    [updateLayout]
  );

  const handleDragStop = (element, d) => {
    updateElement(element.id, (item) => ({
      ...item,
      position: {
        ...item.position,
        x: Math.max(0, d.x / scale),
        y: Math.max(0, d.y / scale),
      },
    }));
  };

  const handleResizeStop = (element, ref, position) => {
    updateElement(element.id, (item) => ({
      ...item,
      position: {
        ...item.position,
        width: parseFloat(ref.style.width) / scale,
        height: parseFloat(ref.style.height) / scale,
        x: position.x / scale,
        y: position.y / scale,
      },
    }));
  };

  const handleStyleChange = (elementId, key, value) => {
    updateElement(elementId, (item) => ({
      ...item,
      style: {
        ...item.style,
        [key]: value,
      },
    }));
  };

  const handleCopyLandingUrl = useCallback(() => {
    if (!landingUrl) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(landingUrl).catch((err) => {
        console.error('Failed to copy landing URL', err);
      });
      return;
    }
    if (typeof document === 'undefined') return;
    const textarea = document.createElement('textarea');
    textarea.value = landingUrl;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-1000px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Failed to copy landing URL', err);
    }
    document.body.removeChild(textarea);
  }, [landingUrl]);

  const handleOpenLandingUrl = useCallback(() => {
    if (!landingUrl || typeof window === 'undefined') return;
    window.open(landingUrl, '_blank', 'noopener');
  }, [landingUrl]);

  const handleDeleteElement = useCallback((id) => {
    updateLayout((current) => ({
      ...current,
      elements: current.elements.filter((el) => el.id !== id),
    }));
    setSelectedId(null);
  }, [updateLayout]);

  const handleAddQrElement = useCallback(() => {
    const id = `qr-${Date.now()}`;
    const newElement = {
      id,
      type: 'qr',
      binding: 'qr',
      position: { x: 100, y: 100, width: 240, height: 240 },
      style: { borderRadius: 16, showLabel: false, background: 'rgba(15,23,42,0.8)' },
    };
    updateLayout((current) => ({
      ...current,
      elements: [...current.elements, newElement],
    }));
    setSelectedId(id);
  }, [updateLayout]);

  const handleAddTextElement = useCallback(() => {
    const id = `custom-${Date.now()}`;
    const newElement = {
      id,
      type: 'text',
      binding: id,
      position: { x: 120, y: 120, width: 700, height: 140 },
      style: {
        fontFamily: 'DM Sans',
        fontSize: 56,
        fontWeight: 500,
        color: '#f8fafc',
        align: 'left',
        lineHeight: 1.2,
        staticText: 'New text block',
      },
    };
    updateLayout((current) => ({
      ...current,
      elements: [...current.elements, newElement],
    }));
    setSelectedId(id);
  }, [updateLayout]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!selectedId || editingId) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteElement(selectedId);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedId, editingId, handleDeleteElement]);

  const commitTextChange = useCallback(
    (element) => {
      const node = textRefs.current.get(element.id);
      if (!node) return;
      const raw = node.innerText.replace(/\r/g, '');
      const normalized = element.binding === 'message' ? raw.trimEnd() : raw.trim();
      // Custom bindings store text directly on the element style
      if (element.binding === element.id || element.style?.staticText !== undefined) {
        updateElement(element.id, (item) => ({
          ...item,
          style: { ...item.style, staticText: normalized },
        }));
      } else if (onContentChange) {
        onContentChange(element.binding, normalized);
      }
    },
    [onContentChange, updateElement]
  );

  const handleTextBlur = useCallback(
    (element) => {
      commitTextChange(element);
      editingInitialValue.current.delete(element.id);
      setEditingId((prev) => (prev === element.id ? null : prev));
    },
    [commitTextChange]
  );

  const handleTextDoubleClick = useCallback(
    (element) => {
      const node = textRefs.current.get(element.id);
      if (!node) return;
      const initial = bindingToText(element.binding, tvContent, element) || '';
      editingInitialValue.current.set(element.id, initial);
      setSelectedId(element.id);
      setEditingId(element.id);
      requestAnimationFrame(() => {
        const target = textRefs.current.get(element.id);
        if (!target) return;
        target.focus();
        const range = document.createRange();
        range.selectNodeContents(target);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });
    },
    [tvContent]
  );

  const handleTextKeyDown = useCallback((element, event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      const original = editingInitialValue.current.get(element.id) || '';
      const node = textRefs.current.get(element.id);
      if (node) {
        node.textContent = original;
        node.blur();
      }
      return;
    }

    if (event.key === 'Enter' && element.binding !== 'message' && !event.shiftKey) {
      event.preventDefault();
      const node = textRefs.current.get(element.id);
      node?.blur();
    }
  }, []);

  const selectedElement = elements.find((element) => element.id === selectedId) || null;

  const renderElement = (element) => {
    const { position, style } = element;
    const width = (position?.width || 200) * scale;
    const height = (position?.height || 120) * scale;
    const x = (position?.x || 0) * scale;
    const y = (position?.y || 0) * scale;
    const scaleValue = (value, defaultValue = 0) => {
      const base = value ?? defaultValue;
      return typeof base === 'number' ? base * scale : base;
    };

    const commonProps = {
      key: element.id,
      bounds: 'parent',
      size: { width, height },
      position: { x, y },
      onDragStop: (_, d) => handleDragStop(element, d),
      onResizeStop: (_, __, ref, ___, pos) => handleResizeStop(element, ref, pos),
      onClick: (e) => {
        e.stopPropagation();
        setSelectedId(element.id);
      },
      onDragStart: () => setSelectedId(element.id),
      onResizeStart: () => setSelectedId(element.id),
      className: `tv-editor-element ${selectedId === element.id ? 'selected' : ''}`,
      style: { cursor: 'move' },
    };

    if (element.type === 'text') {
      const align = style.align || 'left';
      const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      const fontSize = scaleValue(style.fontSize, 32);
      const letterSpacing = scaleValue(style.letterSpacing, 0);
      const rawLineHeight = style.lineHeight;
      const lineHeight = typeof rawLineHeight === 'number'
        ? rawLineHeight > 5
          ? scaleValue(rawLineHeight)
          : rawLineHeight
        : rawLineHeight || 1.2;
      const isEditing = editingId === element.id;
      const assignRef = (node) => {
        if (!node) {
          textRefs.current.delete(element.id);
          return;
        }
        textRefs.current.set(element.id, node);
        if (!isEditing) {
          const text = bindingToText(element.binding, tvContent, element) || '';
          if (node.textContent !== text) {
            node.textContent = text;
          }
        }
      };
      return (
        <Rnd
          {...commonProps}
          enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}
          disableDragging={isEditing}
        >
          <div
            ref={assignRef}
            className="tv-preview-text"
            style={{
              fontFamily: style.fontFamily || 'Inter',
              fontSize,
              fontWeight: style.fontWeight || 500,
              color: style.color || '#f8fafc',
              lineHeight,
              letterSpacing,
              textTransform: style.textTransform || 'none',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: justify,
              textAlign: align,
              height: '100%',
              whiteSpace: 'pre-wrap',
              cursor: isEditing ? 'text' : 'move',
            }}
            contentEditable={isEditing}
            suppressContentEditableWarning
            spellCheck={false}
            onDoubleClick={(event) => {
              event.stopPropagation();
              handleTextDoubleClick(element);
            }}
            onBlur={() => handleTextBlur(element)}
            onKeyDown={(event) => handleTextKeyDown(element, event)}
          >
          </div>
        </Rnd>
      );
    }

    if (element.type === 'image') {
      const borderRadius = scaleValue(style.borderRadius, 32);
      const objFit = style.objectFit || 'cover';
      return (
        <Rnd {...commonProps} enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}>
          <div
            className="tv-preview-visual"
            style={{
              borderRadius,
              background: objFit === 'contain' ? 'transparent' : undefined,
              boxShadow: style.shadow === false ? 'none' : '0 18px 48px rgba(15,23,42,0.35)',
              cursor: 'move',
            }}
          >
            {imageUrl ? (
              <img src={imageUrl} alt="Selected asset" style={{ objectFit: objFit }} />
            ) : (
              <div className="tv-preview-placeholder">Upload an image</div>
            )}
          </div>
        </Rnd>
      );
    }

    if (element.type === 'qr') {
      const borderRadius = scaleValue(style.borderRadius, 0);
      return (
        <Rnd {...commonProps} enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}>
          <div className="tv-preview-qr">
            <div className="tv-preview-qr-code" style={{ borderRadius }}>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="Campaign QR code" draggable={false} />
              ) : (
                <div className="tv-preview-qr-grid" />
              )}
            </div>
          </div>
        </Rnd>
      );
    }

    return (
      <Rnd {...commonProps} enableResizing={false} disableDragging>
        <div className="tv-preview-placeholder">Unsupported element</div>
      </Rnd>
    );
  };

  const handleCanvasBackgroundChange = (color) => {
    updateLayout((current) => ({
      ...current,
      canvas: { ...current.canvas, background: color },
    }));
  };

  const handleGradientUpdate = useCallback((patch) => {
    updateLayout((current) => {
      const canvas = current.canvas || {};
      const next = { ...canvas, ...patch };
      const colorA = next.gradientColorA;
      const colorB = next.gradientColorB;
      if (colorA && colorB) {
        next.backgroundImage = `linear-gradient(${next.gradientDirection || '135deg'}, ${colorA}, ${colorB})`;
      }
      return { ...current, canvas: next };
    });
  }, [updateLayout]);

  const handleClearGradient = useCallback(() => {
    updateLayout((current) => ({
      ...current,
      canvas: {
        ...current.canvas,
        gradientColorA: null,
        gradientColorB: null,
        gradientDirection: null,
        backgroundImage: null,
      },
    }));
  }, [updateLayout]);

  const renderElementControls = () => {
    if (!selectedElement) {
      const hasGradient = !!(layout.canvas?.gradientColorA && layout.canvas?.gradientColorB);
      return (
        <div className="tv-editor-panel">
          <span className="tv-editor-panel-label">Canvas</span>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Background</span>
            <input
              type="color"
              value={layout.canvas?.background || '#0f172a'}
              onChange={(e) => handleCanvasBackgroundChange(e.target.value)}
            />
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Gradient</span>
            <input
              type="color"
              value={layout.canvas?.gradientColorA || '#0d2d4f'}
              onChange={(e) => handleGradientUpdate({ gradientColorA: e.target.value })}
              title="Gradient start colour"
            />
            <span style={{ color: 'rgba(148,163,184,0.5)', fontSize: 11 }}>→</span>
            <input
              type="color"
              value={layout.canvas?.gradientColorB || '#1c1154'}
              onChange={(e) => handleGradientUpdate({ gradientColorB: e.target.value })}
              title="Gradient end colour"
            />
            <select
              value={layout.canvas?.gradientDirection || '135deg'}
              onChange={(e) => handleGradientUpdate({ gradientDirection: e.target.value })}
            >
              <option value="to right">→ Right</option>
              <option value="to bottom">↓ Down</option>
              <option value="135deg">↘ Diagonal</option>
              <option value="to top right">↗ Up-Right</option>
              <option value="to bottom left">↙ Down-Left</option>
            </select>
            {hasGradient && (
              <button type="button" className="tv-editor-align-btn" onClick={handleClearGradient} title="Remove gradient">✕</button>
            )}
          </div>

          <div className="tv-editor-control-group" style={{ marginLeft: 'auto', gap: 6 }}>
            <button type="button" className="tv-editor-add-btn" onClick={handleAddTextElement}>+ Text</button>
            <button type="button" className="tv-editor-add-btn" onClick={handleAddQrElement}>+ QR</button>
          </div>
        </div>
      );
    }

    const { type, style } = selectedElement;

    const labelMap = {
      title: 'Title', subtitle: 'Subtitle', message: 'Message',
      footer: 'Footer', qrLabel: 'QR Label',
    };
    const elementLabel = type === 'text'
      ? (labelMap[selectedElement.binding] || 'Text')
      : type === 'image' ? 'Image' : 'QR Code';

    const deleteBtn = (
      <button
        type="button"
        className="tv-editor-delete-btn"
        onClick={() => handleDeleteElement(selectedElement.id)}
        title="Delete element (Del)"
      >
        Delete
      </button>
    );

    if (type === 'text') {
      const fontOptions = getFontOptions(style.fontFamily);
      const currentAlign = style.align || 'left';
      const currentWeight = style.fontWeight || 500;
      const isCustomBinding = selectedElement.binding === selectedElement.id || style.staticText !== undefined;
      return (
        <div className="tv-editor-panel">
          <span className="tv-editor-panel-label">{elementLabel}</span>

          {!isCustomBinding && (
            <div className="tv-editor-control-group">
              <span className="tv-editor-control-label">Bind</span>
              <select
                value={selectedElement.binding}
                onChange={(e) => updateElement(selectedElement.id, (item) => ({ ...item, binding: e.target.value }))}
              >
                <option value="title">Title</option>
                <option value="subtitle">Subtitle</option>
                <option value="message">Message</option>
                <option value="footer">Footer</option>
                <option value="qrLabel">QR Label</option>
              </select>
            </div>
          )}

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Font</span>
            <FontSelect
              value={style.fontFamily || 'Inter'}
              options={fontOptions}
              onChange={(value) => handleStyleChange(selectedElement.id, 'fontFamily', value)}
            />
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Size</span>
            <input
              type="number"
              className="tv-editor-number-input"
              min={8}
              max={180}
              value={style.fontSize || 32}
              onChange={(e) => handleStyleChange(selectedElement.id, 'fontSize', Number(e.target.value))}
            />
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Weight</span>
            <select
              value={currentWeight}
              onChange={(e) => handleStyleChange(selectedElement.id, 'fontWeight', Number(e.target.value))}
            >
              <option value={300}>Light</option>
              <option value={400}>Regular</option>
              <option value={500}>Medium</option>
              <option value={600}>Semi-Bold</option>
              <option value={700}>Bold</option>
              <option value={800}>Extra Bold</option>
              <option value={900}>Black</option>
            </select>
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Colour</span>
            <input
              type="color"
              value={style.color || '#ffffff'}
              onChange={(e) => handleStyleChange(selectedElement.id, 'color', e.target.value)}
            />
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Align</span>
            <div className="tv-editor-align-buttons">
              {['left', 'center', 'right'].map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`tv-editor-align-btn${currentAlign === a ? ' active' : ''}`}
                  onClick={() => handleStyleChange(selectedElement.id, 'align', a)}
                >
                  {a === 'left' ? 'L' : a === 'center' ? 'C' : 'R'}
                </button>
              ))}
            </div>
          </div>

          <div className="tv-editor-control-group" style={{ marginLeft: 'auto' }}>
            {deleteBtn}
          </div>
        </div>
      );
    }

    if (type === 'image') {
      const shadowOn = style.shadow !== false;
      const objFit = style.objectFit || 'cover';
      return (
        <div className="tv-editor-panel">
          <span className="tv-editor-panel-label">{elementLabel}</span>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Radius</span>
            <input
              type="number"
              className="tv-editor-number-input"
              min={0}
              max={200}
              value={style.borderRadius || 0}
              onChange={(e) => handleStyleChange(selectedElement.id, 'borderRadius', Number(e.target.value))}
            />
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Fit</span>
            <div className="tv-editor-align-buttons">
              {['cover', 'contain'].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`tv-editor-align-btn${objFit === v ? ' active' : ''}`}
                  onClick={() => handleStyleChange(selectedElement.id, 'objectFit', v)}
                >
                  {v === 'cover' ? 'Fill' : 'Fit'}
                </button>
              ))}
            </div>
          </div>

          <div className="tv-editor-control-group">
            <span className="tv-editor-control-label">Shadow</span>
            <div className="tv-editor-align-buttons">
              {['On', 'Off'].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`tv-editor-align-btn${shadowOn === (v === 'On') ? ' active' : ''}`}
                  onClick={() => handleStyleChange(selectedElement.id, 'shadow', v === 'On')}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="tv-editor-control-group" style={{ marginLeft: 'auto' }}>
            {deleteBtn}
          </div>
        </div>
      );
    }

    if (type === 'qr') {
      const link = landingUrl || '';
      return (
        <div className="tv-editor-panel">
          <span className="tv-editor-panel-label">{elementLabel}</span>
          <div className="tv-editor-panel-link">
            <span>{link || 'Landing page link not set yet.'}</span>
            <div className="tv-editor-panel-link-actions">
              <button type="button" onClick={handleCopyLandingUrl} disabled={!link}>Copy</button>
              <button type="button" onClick={handleOpenLandingUrl} disabled={!link}>Open</button>
            </div>
          </div>
          <div className="tv-editor-control-group" style={{ marginLeft: 'auto' }}>
            {deleteBtn}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="tv-editor-wrapper">
      <div className="tv-editor-header">
        <span className="tv-editor-orientation">{canvas.orientation?.toUpperCase() || 'LANDSCAPE'}</span>
        <span className="tv-editor-dimensions">{canvas.width}×{canvas.height}</span>
        <span className="tv-editor-hint" style={{ marginLeft: 'auto' }}>Click to select &middot; Double-click to edit text</span>
      </div>
      <div
        className="tv-editor-canvas"
        ref={containerRef}
        onMouseDown={() => setSelectedId(null)}
      >
        <div className="tv-editor-stage" style={stageStyle}>
          {elements.map(renderElement)}
        </div>
      </div>
      {renderElementControls()}
    </div>
  );
}
